import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useMediaPreview, normType, fmtTime, extractYoutubeId } from './MediaPreviewContext';

// ── Bug-2 fix: instant placeholder + cross-fade + preload-neighbors ───────
// Module-scope (not component state) so the cache survives across
// open/close/navigate within the same page load — same lifetime as the
// browser's own HTTP cache it's mirroring the state of. Capped FIFO (a Map
// preserves insertion order, so deleting the first key evicts the oldest
// entry) — this is a UI-affordance cache (skip the blur+spinner for an
// already-seen image), NOT the actual image bytes; the browser's own HTTP
// cache is what makes the real network request disappear on revisit.
const loadedImageCache = new Map();
const LOADED_CACHE_MAX = 60;
function markImageLoaded(url) {
  if (!url || loadedImageCache.has(url)) return;
  loadedImageCache.set(url, true);
  if (loadedImageCache.size > LOADED_CACHE_MAX) {
    loadedImageCache.delete(loadedImageCache.keys().next().value);
  }
}
function isImageLoaded(url) {
  return !!url && loadedImageCache.has(url);
}
function preloadImage(url) {
  if (!url || loadedImageCache.has(url)) return;
  const img = new Image();
  img.onload = () => markImageLoaded(url);
  img.src = url;
}
function hasRealThumb(item) {
  return !!item?.thumb && item.thumb !== 'loading';
}

/**
 * Full-screen lightbox — ported from .claude/pp-campaign-builder-
 * prototype.html's `.lb`/`.lb-top`/`.lb-stage`/`.lb-media`/`.lb-nav`/
 * `.vp-*`/`.ac`/`.lb-bottom` (lines 306-336) and `lbOpen`/`lbRender`/`lbGo`
 * (~829-863). Renamed to this app's `pp-cb-*` convention (see setup.css).
 *
 * `state` = { list, index, opts } where opts is one of:
 *   - sheet-grid preview: { canSelect, isSel(item), toggle(item), stopAudioOnClose? }
 *   - YouTube preview:    { addLabel, onAdd(item) }
 *   - row preview:        { stopAudioOnClose: true }  (no bottom button)
 */
function Lightbox({ state, onClose, onGo }) {
  const { t } = useTranslation();
  const { audio } = useMediaPreview();
  const { list, index, opts } = state;
  const item = list[index];
  const type = normType(item);
  const n = list.length;

  const closeBtnRef = useRef(null);
  const videoRef = useRef(null);
  const [videoPlaying, setVideoPlaying] = useState(false);
  const [videoMuted, setVideoMuted] = useState(false);
  const [videoPos, setVideoPos] = useState(0);
  const [videoDur, setVideoDur] = useState(0);
  const touchX = useRef(null);
  // See MediaList.jsx's openPreview doc comment: `opts.isSel` reads a live
  // ref, but this component still only re-renders when ITS OWN state
  // changes — this tick forces that re-render right after `toggle`, the
  // same pairing the prototype's own `LB.opts.toggle(o);lbRender();` does.
  const [, forceTick] = useState(0);

  // ── Bug-2 fix state (instant placeholder / cross-fade / preload / retry) ──
  // Same ref-pinned-to-latest pattern MediaList.jsx's own `idsRef` uses (see
  // that file's doc comment, ~line 136) so an async callback (image onLoad,
  // or the video `.play()` promise below) can check the CURRENT index at the
  // moment it resolves instead of the (possibly stale) index closed over
  // when the async op started — a fast swipe through several slides means an
  // older slide's load/canplay event can resolve after the user has already
  // moved past it; this is the race-safety token the task spec calls for.
  const indexRef = useRef(index);
  indexRef.current = index;
  // Forces a re-render after the module-level `loadedImageCache` mutates —
  // a plain Map write doesn't itself trigger React.
  const [, bumpImgTick] = useState(0);
  const erroredUrlsRef = useRef(new Set());
  const [retryTick, setRetryTick] = useState(0);
  const [videoBuffering, setVideoBuffering] = useState(false);

  const isVisualType = type === 'IMAGE' || type === 'GIF' || type === 'CORNER';
  const imgLoaded = isVisualType && isImageLoaded(item?.path);
  const imgErrored = isVisualType && erroredUrlsRef.current.has(item?.path);

  // Background preload of both neighbors — IMAGES ONLY (task spec: video
  // must stay `preload="none"`, so a VIDEO/AUDIO/YOUTUBE neighbor is never
  // touched here), fired once the CURRENT slide has actually finished
  // loading (not eagerly on every navigation) so a fast swipe doesn't queue
  // a pile of neighbor fetches ahead of the image the user is waiting on.
  const preloadNeighbors = (fromIdx) => {
    [fromIdx - 1, fromIdx + 1].forEach((j) => {
      const neighbor = list[j];
      if (!neighbor) return;
      const nType = normType(neighbor);
      if (nType === 'IMAGE' || nType === 'GIF' || nType === 'CORNER') {
        preloadImage(neighbor.path);
      }
    });
  };

  // Already-cached (revisited) slide — preload its neighbors right away
  // since there's no onLoad event coming (the <img> below renders straight
  // into its loaded state).
  useEffect(() => {
    if (isVisualType && isImageLoaded(item?.path)) preloadNeighbors(index);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index]);

  const onFullImgLoad = () => {
    markImageLoaded(item.path);
    erroredUrlsRef.current.delete(item.path);
    preloadNeighbors(index);
    bumpImgTick((v) => v + 1);
  };
  const onFullImgError = () => {
    erroredUrlsRef.current.add(item.path);
    bumpImgTick((v) => v + 1);
  };
  const retryImage = () => {
    erroredUrlsRef.current.delete(item.path);
    setRetryTick((v) => v + 1);
  };

  // Focus-in: the close button, on open and on every nav (matches prototype's
  // lbOpen() focusing #lbX; kept on every render so keyboard users landing on
  // a new slide always have a sane focus target).
  useEffect(() => {
    closeBtnRef.current?.focus();
  }, []);

  // Pause any playing video on navigate/close (task spec: real <video>,
  // "paused automatically on navigate (lbGo) or close (lbClose)").
  useEffect(() => {
    const v = videoRef.current;
    return () => {
      v?.pause();
    };
  }, [index]);

  useEffect(() => {
    setVideoPlaying(false);
    setVideoPos(0);
    setVideoDur(0);
    setVideoBuffering(false);
  }, [index]);

  // Keyboard: Escape close, ArrowRight=prev (RTL-correct, index-1),
  // ArrowLeft=next (index+1), Space toggles play/pause for non-image.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') { onClose(); }
      else if (e.key === 'ArrowRight') { onGo(-1); }
      else if (e.key === 'ArrowLeft') { onGo(1); }
      else if (e.key === ' ' && type !== 'IMAGE' && type !== 'GIF' && type !== 'CORNER') {
        e.preventDefault();
        if (type === 'AUDIO') audio.toggle(item);
        else toggleVideo();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item, type, onClose, onGo]);

  const onTouchStart = (e) => { touchX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    touchX.current = null;
    // Finger moving right (positive dx) = next, per task spec.
    if (Math.abs(dx) > 50) onGo(dx > 0 ? 1 : -1);
  };

  const toggleVideo = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      if (audio.playing) audio.pause(); // starting video pauses any playing audio
      // `preload="none"` (unchanged, per task spec) means there's always a
      // real buffering gap between pressing play and actual playback start —
      // show the spinner for that gap. `myIndex` + the indexRef check below
      // is the race-safety token: if the user has already swiped away by the
      // time this promise resolves, don't flip `videoBuffering` on a since-
      // unmounted/replaced slide.
      const myIndex = index;
      setVideoBuffering(true);
      const playPromise = v.play();
      if (playPromise && typeof playPromise.then === 'function') {
        playPromise
          .then(() => { if (indexRef.current === myIndex) setVideoBuffering(false); })
          .catch(() => { if (indexRef.current === myIndex) setVideoBuffering(false); });
      } else {
        setVideoBuffering(false);
      }
    } else {
      v.pause();
    }
  };

  const metaLabel = type === 'IMAGE' || type === 'GIF' || type === 'CORNER'
    ? t('addcontent.preview_meta_image')
    : type === 'YOUTUBE'
      ? t('addcontent.preview_meta_youtube')
      : type === 'VIDEO'
        ? t('addcontent.preview_meta_video')
        : t('addcontent.preview_meta_audio');
  const durSuffix = type !== 'IMAGE' && type !== 'GIF' && type !== 'CORNER' && item?.duration
    ? ` · ${fmtTime(item.duration)}`
    : '';

  const name = item?.file_name || item?.title || item?.n || '';

  const canSelect = !!opts?.canSelect;
  const isSel = canSelect && opts.isSel(item);

  return (
    <div className="pp-cb-lb show" role="dialog" aria-modal="true" aria-label={name}>
      <div className="lb-top">
        <button type="button" className="lb-x" ref={closeBtnRef} onClick={onClose} aria-label={t('buttons.close')}>
          <i className="pi pi-times" />
        </button>
        <div className="lb-ti">
          <div className="lb-name">{name}</div>
          <div className="lb-meta">{metaLabel}{durSuffix}</div>
        </div>
        {n > 1 && <div className="lb-count">{index + 1} / {n}</div>}
      </div>

      <div className="lb-stage" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
        {isVisualType && (
          <div className="lb-media">
            {/* Instant placeholder — the grid thumbnail this item already has
                (same `thumb` field FileTile's own LazyImage uses), blurred +
                upscaled to fill the stage. Dropped the instant the full-res
                `<img>` below finishes loading (imgLoaded). No prototype
                equivalent exists for this state — see the task report for
                why — built from this component's OWN existing dark-stage
                tokens (see the lb-ph/lb-spin/lb-err rules in setup.css),
                not new colors. */}
            {hasRealThumb(item) && !imgLoaded && (
              <img className="img lb-ph" src={item.thumb} alt="" aria-hidden="true" />
            )}
            {!imgErrored && (
              // Keyed by item id (+ retryTick) so each slide gets a FRESH
              // <img> node — guarantees the outgoing slide's pixels can never
              // linger under the new index's counter, and gives `retryImage`
              // a reliable way to force a new load attempt at the same URL.
              <img
                key={`${item?.id ?? index}-${retryTick}`}
                className="img"
                src={item.path}
                alt={name}
                style={{ opacity: imgLoaded ? 1 : 0, transition: 'opacity .15s ease' }}
                onLoad={onFullImgLoad}
                onError={onFullImgError}
              />
            )}
            {!imgLoaded && !imgErrored && (
              <div className="lb-spin" aria-hidden="true"><i className="pi pi-spin pi-spinner" /></div>
            )}
            {imgErrored && (
              <div className="lb-err">
                <div className="lb-err-icon"><i className="pi pi-exclamation-triangle" /></div>
                <p>{t('addcontent.preview_load_error')}</p>
                <button type="button" onClick={retryImage}>
                  <i className="pi pi-refresh" />{t('buttons.retry')}
                </button>
              </div>
            )}
          </div>
        )}

        {type === 'VIDEO' && (
          <div className="lb-media" style={{ cursor: 'pointer' }}>
            <video
              key={item?.id ?? index}
              ref={videoRef}
              className="img"
              style={{ width: '100%', height: '100%', objectFit: 'contain', background: '#000' }}
              src={item.path}
              poster={hasRealThumb(item) ? item.thumb : undefined}
              preload="none"
              playsInline
              muted={videoMuted}
              onClick={toggleVideo}
              onPlay={() => setVideoPlaying(true)}
              onPlaying={() => { if (indexRef.current === index) setVideoBuffering(false); }}
              onWaiting={() => { if (indexRef.current === index) setVideoBuffering(true); }}
              onPause={() => setVideoPlaying(false)}
              onTimeUpdate={(e) => setVideoPos(e.currentTarget.currentTime || 0)}
              onLoadedMetadata={(e) => setVideoDur(e.currentTarget.duration || 0)}
            />
            {videoBuffering && (
              <div className="lb-spin" aria-hidden="true"><i className="pi pi-spin pi-spinner" /></div>
            )}
            <span className="vp-big" style={{ opacity: videoPlaying || videoBuffering ? 0 : 1, pointerEvents: videoPlaying || videoBuffering ? 'none' : 'auto' }} onClick={toggleVideo}>
              <i className={videoPlaying ? 'pi pi-pause' : 'pi pi-play'} />
            </span>
            <div className="vp-ctl" onClick={(e) => e.stopPropagation()}>
              <button type="button" onClick={toggleVideo} aria-label={t('buttons.play_pause')}>
                <i className={videoPlaying ? 'pi pi-pause' : 'pi pi-play'} />
              </button>
              <div
                className="pbar"
                onClick={(e) => {
                  const r = e.currentTarget.getBoundingClientRect();
                  const frac = Math.min(1, Math.max(0, (e.clientX - r.left) / r.width));
                  if (videoRef.current && videoDur) videoRef.current.currentTime = frac * videoDur;
                }}
              >
                <i style={{ width: videoDur ? `${(videoPos / videoDur) * 100}%` : '0%' }} />
              </div>
              <span className="tm">{fmtTime(videoPos)} / {fmtTime(videoDur)}</span>
              <button
                type="button"
                onClick={() => {
                  setVideoMuted((m) => !m);
                  if (videoRef.current) videoRef.current.muted = !videoMuted;
                }}
                aria-label={t('addcontent.toggle_mute')}
              >
                <i className={videoMuted ? 'pi pi-volume-off' : 'pi pi-volume-up'} />
              </button>
            </div>
          </div>
        )}

        {type === 'AUDIO' && (
          <div className="pp-cb-ac">
            <div className="art"><i className="pi pi-volume-up" /></div>
            <div style={{ fontWeight: 700, fontSize: 16, unicodeBidi: 'plaintext' }}>{name}</div>
            <div
              className="pbar"
              onClick={(e) => {
                if (audio.item !== item) audio.play(item);
                const r = e.currentTarget.getBoundingClientRect();
                const frac = Math.min(1, Math.max(0, (e.clientX - r.left) / r.width));
                audio.seek(frac);
              }}
            >
              <i style={{ width: `${audio.item === item && audio.dur ? (audio.pos / audio.dur) * 100 : 0}%` }} />
            </div>
            <div className="tms">
              <span>{fmtTime(audio.item === item ? audio.pos : 0)}</span>
              <span>{fmtTime(audio.item === item ? audio.dur : item?.duration)}</span>
            </div>
            <button type="button" className="apb" onClick={() => audio.toggle(item)} aria-label={t('buttons.play_pause')}>
              <i className={audio.item === item && audio.playing ? 'pi pi-pause' : 'pi pi-play'} />
            </button>
          </div>
        )}

        {type === 'YOUTUBE' && (
          <div className="lb-media">
            <iframe
              className="img"
              style={{ border: 0 }}
              src={`https://www.youtube.com/embed/${item.youtubeId || extractYoutubeId(item.path)}?autoplay=0`}
              title={name}
              allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        )}

        {n > 1 && (
          <>
            <button type="button" className="lb-nav lb-prev" onClick={() => onGo(-1)} disabled={index === 0} aria-label={t('buttons.previous')}>
              <i className="bx bx-chevron-right" />
            </button>
            <button type="button" className="lb-nav lb-next" onClick={() => onGo(1)} disabled={index === n - 1} aria-label={t('buttons.next')}>
              <i className="bx bx-chevron-right" style={{ transform: 'scaleX(-1)' }} />
            </button>
          </>
        )}
      </div>

      <div className="lb-bottom">
        {canSelect && (
          <button type="button" className={`btn btn-primary lb-sel${isSel ? ' on' : ''}`} onClick={() => { opts.toggle(item); forceTick((n) => n + 1); }}>
            <i className={isSel ? 'pi pi-check' : 'pi pi-plus'} />
            {isSel ? t('addcontent.preview_selected') : t('addcontent.preview_select')}
          </button>
        )}
        {!canSelect && opts?.addLabel && (
          <button
            type="button"
            className="btn btn-primary lb-sel"
            onClick={() => { opts.onAdd(item); onClose(); }}
          >
            <i className="pi pi-plus" />
            {opts.addLabel}
          </button>
        )}
      </div>
    </div>
  );
}

export default Lightbox;
